/**
 * A client's standing commercial terms, as one set of controls.
 *
 * Shared rather than written twice. Adding a client and editing one are two
 * screens over one endpoint, and every field duplicated between them is a
 * field that eventually disagrees — the tax rates and payment terms are
 * already spelled out separately in both places, which is how the add form
 * ended up offering a free-text payment term while the edit dialog offered
 * the same.
 *
 * These are terms agreed once with a client and then applied per placement.
 * Nothing here computes or applies anything: the fee they describe is copied
 * into a placement's Agreed fee box as a *suggestion* somebody confirms, never
 * written on their behalf. See TrackerPage's edit dialog for where that
 * happens, and offer_service's docstring for why it does not happen at
 * placement creation.
 */
import type { FeeBasis } from '../types';
import {
  AGREEMENT_ACTIVE,
  AGREEMENT_HINTS,
  agreementOptions,
} from '../lib/requirementOptions';

/**
 * What the replacement-period dropdown offers.
 *
 * 45/60/90 are what the client's specification asks for. 30 stays because it
 * is agreed with clients in the live book, and dropping it from the list would
 * not remove it from them — it would only make the dropdown unable to say what
 * they are on.
 *
 * The column is a plain integer rather than an enum because the number is
 * negotiated, so `options` below injects any stored value that is off this
 * list rather than silently snapping it to a neighbour.
 */
export const REPLACEMENT_DAYS = [30, 45, 60, 90];

/**
 * What the payment-tenure dropdown offers.
 *
 * 30/45/60 are what the client's specification asks for. 60 was previously
 * kept off this list on the grounds that it was not offered as a term; the
 * specification now offers it, so it is here. The four clients in the live
 * book already on 60-day terms stop relying on `options` to be representable.
 *
 * 15 stays for the same reason 30 stays on the replacement list: it is in use,
 * and removing it from the dropdown would not remove it from them.
 */
export const PAYMENT_TERM_DAYS = [15, 30, 45, 60];

/**
 * The offered values, plus the current one when it is not among them.
 *
 * A select that cannot represent the value it was given is a control that
 * edits data by being looked at.
 */
export function options(offered: number[], current: number | null): number[] {
  if (current === null || offered.includes(current)) return offered;
  return [...offered, current].sort((a, b) => a - b);
}

export interface CommercialTerms {
  feeBasis: FeeBasis | '';
  /**
   * What the client has agreed to, or '' for nothing recorded.
   *
   * Here rather than beside the contact details because it belongs with
   * the commercials: a fee, a payment tenure and a replacement clause are
   * the terms, and this says whether those terms were actually agreed.
   */
  agreementState: string;
  feePercent: string;
  flatAmount: string;
  paymentTerms: string;
  replacementDays: string;
}

export const EMPTY_TERMS: CommercialTerms = {
  // Empty rather than 'none': a new client nobody has asked about has no
  // recorded answer, and 'none' would hide it from every recruiter the moment
  // it was created.
  agreementState: '',
  feeBasis: '',
  feePercent: '',
  flatAmount: '',
  // The model's own default, so a client created without touching this field
  // gets the same 30 days the column would have given it.
  paymentTerms: '30',
  replacementDays: '',
};

/** Turn the form's strings into the payload the API expects. */
export function termsPayload(t: CommercialTerms): Record<string, unknown> {
  const basis = t.feeBasis || null;
  return {
    agreementState: t.agreementState || null,
    defaultFeeBasis: basis,
    // Only the branch the basis names is sent. Posting both would leave a
    // stale figure in the column nobody reads, waiting to be mistaken for the
    // agreed rate the first time somebody switched the basis back.
    defaultFeePercent:
      basis === 'ctc_percent' && t.feePercent.trim() !== ''
        ? t.feePercent.trim()
        : null,
    defaultBillingAmount:
      basis === 'flat' && t.flatAmount.trim() !== '' ? t.flatAmount.trim() : null,
    paymentTermsDays: t.paymentTerms.trim() === '' ? 30 : Number(t.paymentTerms),
    defaultReplacementDays:
      t.replacementDays.trim() === '' ? null : Number(t.replacementDays),
  };
}

export default function CommercialTermsFields({
  value,
  onChange,
}: {
  value: CommercialTerms;
  onChange: (next: CommercialTerms) => void;
}) {
  const set = <K extends keyof CommercialTerms>(key: K, v: CommercialTerms[K]) =>
    onChange({ ...value, [key]: v });

  const currentTerms = value.paymentTerms.trim() === ''
    ? null
    : Number(value.paymentTerms);
  const currentReplacement = value.replacementDays.trim() === ''
    ? null
    : Number(value.replacementDays);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {/* First, because it governs the three below it: terms nobody
          agreed to are terms nothing should be applied from. And because
          "Nothing agreed" hides the client from recruiters, which is the
          most consequential thing on this form -- the hint under it says
          so in words rather than leaving it to be discovered. */}
      <label className="text-xs text-gray-600">
        <span className="flex items-center justify-between gap-2">
          Agreement Status
          {/* One word for the consequence, because signed and mail-confirmed
              have the same one: the agreement is in force and recruiters work
              the client. The dropdown keeps both, since only one of them can
              be produced if the terms are ever disputed. */}
          {AGREEMENT_ACTIVE.includes(value.agreementState) && (
            <span className="badge bg-emerald-100 text-emerald-700">Active</span>
          )}
        </span>
        <select
          className="input mt-1"
          value={value.agreementState}
          onChange={(e) => set('agreementState', e.target.value)}
        >
          {/* Null is a real fourth state, not an empty box: nobody has
              answered, carry on as before, which is true of nearly every
              client on file. The hint below the control is what says so now. */}
          <option value="">Select Status</option>
          {agreementOptions(value.agreementState).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span
          className={`mt-0.5 block text-[11px] ${
            value.agreementState === 'none' ? 'text-amber-700' : 'text-gray-400'
          }`}
        >
          {AGREEMENT_HINTS[value.agreementState] ?? AGREEMENT_HINTS['']}
        </span>
      </label>

      <label className="text-xs text-gray-600">
        Sourcing Fee Basis
        <select
          className="input mt-1"
          value={value.feeBasis}
          onChange={(e) => set('feeBasis', e.target.value as FeeBasis | '')}
        >
          <option value="">Not Agreed</option>
          <option value="ctc_percent">% Of CTC</option>
          <option value="flat">Flat Fee</option>
        </select>
        <span className="mt-0.5 block text-[11px] text-gray-400">
          Suggested on each placement; never applied on its own.
        </span>
      </label>

      {/* Only the branch the basis names is shown. A percentage box beside a
          flat fee is a question with no answer, and leaving both visible is
          how the two columns end up disagreeing. */}
      {value.feeBasis === 'ctc_percent' && (
        <label className="text-xs text-gray-600">
          Sourcing Fee %
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="decimal"
            placeholder="8.33"
            value={value.feePercent}
            onChange={(e) => set('feePercent', e.target.value)}
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            Of annual CTC. 6 means 6%.
          </span>
        </label>
      )}

      {value.feeBasis === 'flat' && (
        <label className="text-xs text-gray-600">
          Flat Fee
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="decimal"
            placeholder="21000"
            value={value.flatAmount}
            onChange={(e) => set('flatAmount', e.target.value)}
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            Per placement, in rupees.
          </span>
        </label>
      )}

      <label className="text-xs text-gray-600">
        Payment Tenure
        <select
          className="input mt-1"
          value={value.paymentTerms}
          onChange={(e) => set('paymentTerms', e.target.value)}
        >
          {options(PAYMENT_TERM_DAYS, currentTerms).map((d) => (
            <option key={d} value={String(d)}>{d} Days</option>
          ))}
        </select>
        <span className="mt-0.5 block text-[11px] text-gray-400">
          Sets the due date on their invoices.
        </span>
      </label>

      <label className="text-xs text-gray-600">
        Replacement Clause
        <select
          className="input mt-1"
          value={value.replacementDays}
          onChange={(e) => set('replacementDays', e.target.value)}
        >
          {/* The blank option stays, with neutral wording. Removing it
              outright would leave a select whose value is '' rendering as
              though the first real figure were chosen — the control would
              show a term nobody agreed while the state said none. The column
              is nullable and null means no standing terms; that has to remain
              sayable. */}
          <option value="">Select…</option>
          {options(REPLACEMENT_DAYS, currentReplacement).map((d) => (
            <option key={d} value={String(d)}>{d} Days</option>
          ))}
        </select>
        <span className="mt-0.5 block text-[11px] text-gray-400">
          A drop inside this window usually voids the fee.
        </span>
      </label>
    </div>
  );
}
